import {
  collection,
  doc,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  query,
  orderBy,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';

export interface TripTransaction {
  id: string;
  name: string;
  amount: number;
  categoryId: string;
  createdAt: Date;
  notes?: string;
}

export interface TripData {
  id: string;
  name: string;
  totalFund: number;
  currency: string;
  startDate: string;
  endDate: string;
  adminUid: string;
  createdAt: Date;
}

export async function createTrip(
  adminUid: string,
  data: Omit<TripData, 'id' | 'adminUid' | 'createdAt'>
): Promise<string> {
  const tripRef = doc(collection(db, 'trips'));
  await setDoc(tripRef, {
    ...data,
    adminUid,
    createdAt: serverTimestamp(),
  });
  return tripRef.id;
}

/** Fetch all trips created by this admin (one-time read, newest first). */
export async function getAdminTrips(adminUid: string): Promise<TripData[]> {
  const q = query(
    collection(db, 'trips'),
    where('adminUid', '==', adminUid),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      name: data.name,
      totalFund: data.totalFund,
      currency: data.currency,
      startDate: data.startDate,
      endDate: data.endDate,
      adminUid: data.adminUid,
      createdAt: (data.createdAt as Timestamp)?.toDate?.() ?? new Date(),
    };
  });
}

export async function addTripTransaction(
  tripId: string,
  tx: Omit<TripTransaction, 'id' | 'createdAt'>
): Promise<void> {
  const col = collection(db, `trips/${tripId}/transactions`);
  await addDoc(col, { ...tx, createdAt: serverTimestamp() });
}

/** Update an existing transaction's editable fields. */
export async function updateTripTransaction(
  tripId: string,
  txId: string,
  updates: Partial<Pick<TripTransaction, 'name' | 'amount' | 'categoryId' | 'notes'>>
): Promise<void> {
  const ref = doc(db, `trips/${tripId}/transactions`, txId);
  await updateDoc(ref, updates);
}

/** Delete a transaction from a trip. */
export async function deleteTripTransaction(
  tripId: string,
  txId: string
): Promise<void> {
  const ref = doc(db, `trips/${tripId}/transactions`, txId);
  await deleteDoc(ref);
}

export function subscribeTripData(
  tripId: string,
  onData: (trip: TripData, transactions: TripTransaction[]) => void,
  onError: (e: Error) => void
): () => void {
  const tripDoc = doc(db, 'trips', tripId);
  let transactions: TripTransaction[] = [];
  let tripData: TripData | null = null;

  const unsubTrip = onSnapshot(tripDoc, (snap) => {
    if (!snap.exists()) { onError(new Error('Trip not found')); return; }
    const d = snap.data();
    tripData = {
      id: snap.id,
      name: d.name,
      totalFund: d.totalFund,
      currency: d.currency,
      startDate: d.startDate,
      endDate: d.endDate,
      adminUid: d.adminUid,
      createdAt: (d.createdAt as Timestamp)?.toDate?.() ?? new Date(),
    };
    if (tripData) onData(tripData, transactions);
  }, onError);

  const txCol = collection(db, `trips/${tripId}/transactions`);
  const txQuery = query(txCol, orderBy('createdAt', 'desc'));
  const unsubTx = onSnapshot(txQuery, (snap) => {
    transactions = snap.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        name: data.name,
        amount: data.amount,
        categoryId: data.categoryId,
        notes: data.notes,
        createdAt: (data.createdAt as Timestamp)?.toDate?.() ?? new Date(),
      };
    });
    if (tripData) onData(tripData, transactions);
  }, onError);

  return () => { unsubTrip(); unsubTx(); };
}
